import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CTAButton from '../../components/base/CTAButton';
import { useSEO } from '../../utils/seo';

export default function CinewallShowroom() {
  useSEO({
    title: 'Cinewall showroom | Persoonlijk advies',
    description: 'Bezoek onze cinewall showroom en ervaar materialen en haarden in het echt.',
    keywords: 'cinewall showroom, cinewall bekijken, cinewall advies, showroom afspraak',
    canonical: `${import.meta.env.VITE_SITE_URL}/cinewall-showroom`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'cinewallkopen.nl Showroom',
      description: 'Bezoek onze showroom en ervaar materialen, haarden en verlichting in het echt',
      url: `${import.meta.env.VITE_SITE_URL}/cinewall-showroom`,
      areaServed: {
        '@type': 'Country',
        name: 'Nederland',
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Cinewall showroom',
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Showroombezoek',
              description: 'Persoonlijk advies en materialen bekijken',
            },
          },
        ],
      },
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);

    // Load Calendly widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const showroomFeatures = [
    {
      icon: 'ri-eye-line',
      title: 'Materialen ervaren',
      description: 'Voel en zie de kwaliteit van natuursteen, hout en afwerkingen in het echt'
    },
    {
      icon: 'ri-fire-line',
      title: 'Haarden bekijken',
      description: 'Ervaar het verschil tussen elektrische en waterdamp haarden live'
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Verlichting testen',
      description: 'Zie hoe LED-verlichting sfeer en diepte toevoegt aan uw cinewall'
    },
    {
      icon: 'ri-user-line',
      title: 'Persoonlijk advies',
      description: 'Bespreek uw wensen en ontvang direct technisch en esthetisch advies'
    },
    {
      icon: 'ri-pencil-ruler-2-line',
      title: 'Ontwerp bespreken',
      description: 'Bekijk voorbeelden en bespreek de mogelijkheden voor uw woning'
    },
    {
      icon: 'ri-question-answer-line',
      title: 'Al uw vragen',
      description: 'Neem de tijd om alles te vragen over techniek, prijs en planning'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-serif text-gray-900 mb-6">
            Bezoek onze showroom
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
            Een cinewall kiest u niet online. Kom langs in onze showroom en ervaar materialen, 
            haarden en verlichting in het echt. Wij nemen uitgebreid de tijd voor persoonlijk advies.
          </p>
        </div>
      </section>

      {/* Local SEO Section - NEW */}
      <section className="pb-12 px-6 bg-stone-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-6">
            Cinewall showroom in Nederland
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            In onze <strong>showroom</strong> kunt u materialen, afwerkingen en haarden in het echt bekijken.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            Een showroomafspraak is geheel vrijblijvend en bedoeld om u persoonlijk te adviseren 
            over uw <strong>cinewall op maat</strong>.
          </p>
        </div>
      </section>

      {/* Calendly Section - MOVED TO TOP */}
      <section className="py-20 px-6 bg-stone-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-serif text-gray-900 mb-6">
              Plan uw showroomafspraak
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Plan eenvoudig uw showroomafspraak op een moment dat u uitkomt.
              Na het plannen ontvangt u direct een bevestiging per e-mail.
            </p>
          </div>

          {/* Calendly Embed */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden" style={{ minHeight: '700px' }}>
            <div 
              className="calendly-inline-widget" 
              data-url="https://calendly.com/diamondflame/afspraak-in-onze-showroom-1" 
              style={{ minWidth: '320px', height: '700px' }}
            ></div>
          </div>

          {/* Trust Elements */}
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
            <div className="flex flex-col items-center">
              <i className="ri-time-line text-3xl text-[#C7A86A] mb-3 w-8 h-8 flex items-center justify-center"></i>
              <p className="text-gray-700 font-semibold">Flexibele tijden</p>
              <p className="text-sm text-gray-600">Kies een moment dat u uitkomt</p>
            </div>
            <div className="flex flex-col items-center">
              <i className="ri-user-heart-line text-3xl text-[#C7A86A] mb-3 w-8 h-8 flex items-center justify-center"></i>
              <p className="text-gray-700 font-semibold">Persoonlijk advies</p>
              <p className="text-sm text-gray-600">Uitgebreid en zonder haast</p>
            </div>
            <div className="flex flex-col items-center">
              <i className="ri-mail-check-line text-3xl text-[#C7A86A] mb-3 w-8 h-8 flex items-center justify-center"></i>
              <p className="text-gray-700 font-semibold">Directe bevestiging</p>
              <p className="text-sm text-gray-600">Per e-mail en kalender</p>
            </div>
          </div>
        </div>
      </section>

      {/* Showroom Image */}
      <section className="py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="aspect-[16/9] rounded-lg overflow-hidden shadow-2xl">
            <img 
              src="https://public.readdy.ai/ai/img_res/af83b30236cc2bfd0789c7eab1930af2.jpg"
              alt="Cinewall showroom"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* What You Can See */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif text-gray-900 mb-4">
              Wat u kunt bekijken in de showroom
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Ontdek alle mogelijkheden en maak weloverwogen keuzes
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {showroomFeatures.map((feature, index) => (
              <div key={index} className="bg-stone-50 p-8 rounded-lg">
                <div className="w-14 h-14 flex items-center justify-center bg-white rounded-lg mb-6">
                  <i className={`${feature.icon} text-2xl text-stone-800`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Visit */}
      <section className="py-20 px-6 bg-stone-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-serif text-gray-900 mb-12 text-center">
            Waarom een showroombezoek?
          </h2>
          
          <div className="space-y-8">
            <div className="bg-white p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Materialen zijn online niet te beoordelen
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Foto's doen geen recht aan de kwaliteit en uitstraling van natuursteen, houtsoorten 
                en afwerkingen. In onze showroom voelt en ziet u het verschil tussen standaard en premium.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Haarden live ervaren
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Het verschil tussen een elektrische haard en een waterdamp haard is groot. 
                Wij laten u beide opties zien, zodat u een weloverwogen keuze kunt maken.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Persoonlijk advies op maat
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Tijdens het showroombezoek bespreken we uw wensen, woning en budget. 
                U krijgt direct technisch en esthetisch advies, afgestemd op uw situatie.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Vertrouwen en vakmanschap
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Een cinewall is een investering. Door ons te ontmoeten en onze werkwijze te zien, 
                krijgt u vertrouwen in de kwaliteit en professionaliteit die wij leveren.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-serif text-gray-900 mb-12 text-center">
            Zo werkt een showroombezoek
          </h2>
          
          <div className="space-y-6">
            <div className="flex items-start">
              <div className="w-12 h-12 flex items-center justify-center bg-stone-800 text-white rounded-full font-semibold text-lg flex-shrink-0 mr-6">
                1
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Plan uw afspraak
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Gebruik de Calendly-planner hierboven om een moment te kiezen dat u uitkomt, 
                  zonder haast of tijdsdruk.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 flex items-center justify-center bg-stone-800 text-white rounded-full font-semibold text-lg flex-shrink-0 mr-6">
                2
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Bekijk en ervaar
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Wij laten u alle materialen, haarden en verlichtingsopties zien. 
                  U krijgt de tijd om alles rustig te bekijken en te vergelijken.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 flex items-center justify-center bg-stone-800 text-white rounded-full font-semibold text-lg flex-shrink-0 mr-6">
                3
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Bespreek uw wensen
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Vertel ons over uw woning, stijl en budget. Wij adviseren u over de beste opties 
                  en geven een realistische prijsindicatie.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 flex items-center justify-center bg-stone-800 text-white rounded-full font-semibold text-lg flex-shrink-0 mr-6">
                4
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Ontvang een offerte
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Na het bezoek ontvangt u een gedetailleerde offerte op maat. 
                  Geen verrassingen, alleen duidelijkheid.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
